import { safeParseJson, toApiError } from "@/lib/api/client"

export interface ClientApiFetchOptions {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE"
  body?: unknown
  headers?: Record<string, string>
  cache?: RequestCache
  /**
   * When true (default), a 401 response ends the session: the local SSE token is
   * cleared and the browser is sent to /api/auth/expire, which clears the auth
   * cookies and redirects to /login. Set to false for the login/signup flow,
   * where a 401 means bad credentials and must stay displayable on the form.
   */
  redirectOnUnauthorized?: boolean
  /** Lets a caller abort the in-flight request. */
  signal?: AbortSignal
}

let isExpiring = false

function handleSessionExpired(): void {
  if (isExpiring) return
  isExpiring = true
  // Mirror logout(): drop the SSE token (EventSource can't read HttpOnly cookies)
  // before handing off to the route that clears the cookies and redirects.
  try {
    localStorage.removeItem("auth_token")
  } catch {
    // Storage unavailable — ignore.
  }
  // Reuse the existing route that clears the auth cookies and redirects to /login.
  window.location.replace("/api/auth/expire")
}

/**
 * Shared fetch wrapper for client-side calls to the app's own Next.js route
 * handlers (e.g. /api/projects). Parses the JSON body, throws an ApiError on a
 * non-OK response, and — crucially — turns a 401 into a redirect to /login
 * instead of letting the raw error ("Signature has expired") render inline.
 *
 * The server-side path centralizes the same behavior via withAuthRedirect().
 */
export async function clientApiFetch<T>(
  path: string,
  {
    method = "GET",
    body,
    headers,
    cache,
    redirectOnUnauthorized = true,
    signal,
  }: ClientApiFetchOptions = {},
): Promise<T> {
  const finalHeaders: Record<string, string> = {
    Accept: "application/json",
    ...headers,
  }
  if (body !== undefined) {
    finalHeaders["Content-Type"] = "application/json"
  }

  const response = await fetch(path, {
    method,
    headers: finalHeaders,
    cache,
    signal,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  })

  const text = await response.text()
  const data = safeParseJson(text)

  if (!response.ok) {
    if (
      response.status === 401 &&
      redirectOnUnauthorized &&
      typeof window !== "undefined"
    ) {
      handleSessionExpired()
      // The page is navigating away; never resolve so the caller's catch block
      // doesn't flash the now-stale session error before the redirect lands.
      return new Promise<never>(() => {})
    }
    throw toApiError(data, response.status)
  }

  return data as T
}
